// frontend/src/queries/dataChanged.js
//
// A save names the KINDS of store data it changed (`DATA`); this module knows
// who reads each kind and makes them read again. Two caches hold what the
// pages show: the TanStack query cache, and the blocks each server-side AG
// Grid keeps for itself (`hooks/useSsrmGrid`), which no query invalidation
// reaches. Both are reached from here — `QUERY_READS` lists what each query
// reads, `GRID_READS` what each grid endpoint reads — so a save never lists
// screens or query keys, and a reader is registered once, here. This covers
// the saves made in this browser; a change from outside (a nightly run, a
// webhook, another user) is not announced here.
//
//   const changed = useDataChanged();
//   await changed([DATA.supplierLinks, DATA.items]);
//
// A write that changes rows of a grid in place, adding and removing none,
// also names that grid's endpoint (`edits`); the grid then reads the rows it
// shows again by id and keeps them where they stand.
//
// `changed` resolves once the queries on screen that read a changed kind have
// been read again, so a caller that must show the server's values and nothing
// older awaits it. A reader outside the query cache hears a change through
// `useOnDataChanged`.
//
// An entry lists the kinds of data the endpoint's backend view, serializers
// and services read (filters, annotations, totals blocks and the nested
// serializers included), traced in that code; a new reader is registered the
// same way, from what its backend reads. Over-listing costs one background
// refetch; under-listing leaves a screen stale, which is the fault this module
// exists to remove — so a kind the backend only might read stays listed.
// The tests hold both tables complete: every store-scoped query prefix in
// `queries/keys.js` is in QUERY_READS or NOT_CHANGED_BY_SAVES, and every grid
// endpoint in the app is in GRID_READS.

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import * as keys from './keys';

export const DATA = Object.freeze({
  // An item's own fields: codes, names, status, flags, category, sell price
  // and cost of record.
  items: 'items',
  // An item at a location: stock on hand and the placement's planning
  // figures (minimum stock, safety stock, lead time, pack sizes).
  inventory: 'inventory',
  suppliers: 'suppliers',
  // Which suppliers an item has, which one is its default, and each
  // relation's terms.
  supplierLinks: 'supplierLinks',
  locations: 'locations',
  // Calendars with their holidays and exceptions.
  calendars: 'calendars',
  // The store's category tree and where it comes from.
  categories: 'categories',
  // Headers, lines, receipts and status, and with them the stock on the way.
  purchaseOrders: 'purchaseOrders',
  // The pending recommendations: their headers, lines and status.
  recommendations: 'recommendations',
  // A calculation run replaced every pending recommendation with new ids.
  // That is a change of the recommendations too, so it reaches every reader
  // of `recommendations` (`affects`); only a reader whose row ids it kills —
  // the recommendations grid, which purges on it — lists it.
  recommendationRun: 'recommendationRun',
  promotions: 'promotions',
  // Forecast rows and the merchant's overrides of them.
  forecasts: 'forecasts',
  // The Store settings payload.
  settings: 'settings',
  // Which Data Health issues the store ignores.
  dataHealth: 'dataHealth',
  // Suspected duplicate item pairs.
  identity: 'identity',
  connectors: 'connectors',
  // The signed-in person's own profile.
  profile: 'profile',
  // The whole store changed underneath the app (an import ended): every
  // reader reads again, whatever it lists.
  everything: 'everything',
});

const D = DATA;
const KINDS = new Set(Object.values(DATA));

// Each query's prefix (queries/keys.js) and the kinds of data its endpoint
// reads. Every reader of a report, a grid or a money figure reads `settings`:
// the store's currency and timezone shape each of them.
export const QUERY_READS = [
  [keys.suppliers.all, [D.suppliers, D.connectors, D.settings]],
  [keys.locations.all, [D.locations, D.calendars, D.connectors, D.settings]],
  [keys.calendars.all, [D.calendars]],
  [keys.calendarHolidays.all, [D.calendars]],
  [keys.calendarExceptions.all, [D.calendars]],
  [keys.productCategories.all, [D.categories, D.settings]],
  [keys.productCategories.inStoreAll, [D.items]],
  [keys.supplierCatalogItems.all, [D.supplierLinks, D.suppliers, D.items, D.settings]],
  [keys.kpi.inventory, [D.inventory, D.items, D.dataHealth, D.settings]],
  [keys.kpi.forecast, [D.forecasts, D.items, D.settings]],
  [keys.kpi.sales, [D.settings]],
  [keys.kpi.shortage, [
    D.inventory, D.items, D.forecasts, D.supplierLinks, D.suppliers, D.locations,
    D.purchaseOrders, D.dataHealth, D.settings,
  ]],
  [keys.kpi.excess, [
    D.inventory, D.items, D.forecasts, D.supplierLinks, D.suppliers, D.locations,
    D.purchaseOrders, D.dataHealth, D.settings,
  ]],
  // The health signal is the store's Data Health snapshot, which the server
  // recomputes from every kind its issues count.
  [keys.notifications.badge, [
    D.items, D.inventory, D.suppliers, D.supplierLinks, D.locations, D.forecasts,
    D.promotions, D.purchaseOrders, D.recommendations, D.dataHealth, D.identity,
    D.connectors, D.settings,
  ]],
  [keys.homeTables.shortageItems, [
    D.inventory, D.items, D.forecasts, D.supplierLinks, D.suppliers, D.locations,
    D.purchaseOrders, D.settings,
  ]],
  [keys.homeTables.outstandingPOs, [D.purchaseOrders, D.suppliers, D.locations, D.settings]],
  [keys.reorderLevels.all, [D.inventory, D.forecasts, D.items, D.locations, D.settings]],
  // The one-pair drill-down also draws the target line, which the planning
  // chain (default supplier, calendars, store defaults) decides.
  [keys.timelines.inventoryAll, [
    D.inventory, D.items, D.forecasts, D.categories, D.supplierLinks, D.suppliers,
    D.locations, D.calendars, D.purchaseOrders, D.recommendations, D.promotions,
    D.dataHealth, D.settings,
  ]],
  [keys.timelines.demandAll, [
    D.forecasts, D.items, D.categories, D.locations, D.promotions, D.dataHealth, D.settings,
  ]],
  [keys.timelines.salesProgressAll, [D.items, D.categories, D.settings]],
  [keys.dataBounds.all, [D.forecasts, D.settings]],
  [keys.promotions.all, [D.promotions, D.items]],
  // The detail and its issue date: the order, its lines' items and the
  // supplier's lead time and delivery calendar.
  [keys.purchaseOrders.all, [
    D.purchaseOrders, D.suppliers, D.supplierLinks, D.items, D.locations, D.calendars,
    D.connectors, D.settings,
  ]],
  [keys.poReceiptHistory.all, [D.purchaseOrders, D.items, D.profile]],
  [keys.poPlanningSummary.all, [
    D.recommendations, D.supplierLinks, D.suppliers, D.items, D.inventory, D.forecasts,
    D.locations, D.identity, D.dataHealth, D.settings, D.connectors,
  ]],
  [keys.poRecommendationLines.all, [D.recommendations, D.items, D.suppliers, D.locations, D.settings]],
  // Each line carries the item's full record, default supplier included.
  [keys.customerOrderLines.all, [
    D.items, D.supplierLinks, D.suppliers, D.categories, D.locations, D.connectors,
    D.settings, D.profile,
  ]],
  [keys.dataHealthSummary.all, [
    D.dataHealth, D.items, D.inventory, D.suppliers, D.supplierLinks, D.locations,
    D.forecasts, D.promotions, D.identity, D.connectors, D.settings,
  ]],
  [keys.identityDuplicates.all, [D.identity, D.items]],
  [keys.forecastStatistics.all, [D.items, D.inventory, D.locations]],
  [keys.storeSettings.all, [D.settings, D.categories]],
  [keys.exportColumns.all, [D.settings]],
  // The list itself reads only the connectors; the activity and the Exact
  // review screens under the same prefix count and match the store's rows.
  [keys.connectors.all, [
    D.connectors, D.suppliers, D.supplierLinks, D.locations, D.items, D.purchaseOrders,
  ]],
  [keys.userProfile.me, [D.profile, D.settings]],
  [keys.users.all, [D.profile]],
];

// Store-scoped query prefixes that no save in this browser changes: each
// reads only rows the server's own runs write (sync runs, pipeline runs,
// export jobs, billing) or rows the app has no form for (location types).
// Only `everything` reaches them.
export const NOT_CHANGED_BY_SAVES = [
  keys.syncStatus.all,
  keys.billingPricing.all,
  keys.exportJobs.all,
  keys.costImport.status,
  keys.vendorSuppliers.status,
  keys.locationTypes.all,
];

// Each server-side grid's endpoint (the `endpoint` it gives useSsrmGrid) and
// the kinds its rows, figures and totals read. `purgeOn` names the kinds
// whose change leaves the grid's row ids dead: the rows are thrown away and
// the grid starts from the top; on every other change the loaded blocks are
// read again in place.
export const GRID_READS = {
  // Every page of rows also carries the store's active promotions.
  '/api/item-locations/': {
    reads: [
      D.items, D.inventory, D.suppliers, D.supplierLinks, D.locations, D.categories,
      D.calendars, D.connectors, D.promotions, D.settings, D.profile,
    ],
  },
  '/api/items/': {
    reads: [D.items, D.supplierLinks, D.suppliers, D.categories, D.connectors, D.settings, D.profile],
  },
  // Stock on the way is the inventory columns the purchase-order signals
  // keep, not the order lines themselves.
  '/api/inventory/': {
    reads: [
      D.inventory, D.items, D.locations, D.categories, D.supplierLinks, D.suppliers,
      D.purchaseOrders, D.settings,
    ],
  },
  '/api/locations/': { reads: [D.locations, D.calendars, D.connectors, D.settings] },
  '/api/suppliers/': { reads: [D.suppliers, D.connectors, D.settings] },
  '/api/supplier-catalog-items/': { reads: [D.supplierLinks, D.suppliers, D.items, D.settings] },
  '/api/po-calculations/purchase-orders/': {
    reads: [
      D.purchaseOrders, D.suppliers, D.items, D.locations, D.calendars, D.connectors,
      D.categories, D.settings,
    ],
  },
  // Each picker row carries the item's stock at the destination and its
  // net demand for the next 30 days.
  '/api/po-calculations/purchase-orders/supplier_catalog/': {
    reads: [
      D.supplierLinks, D.items, D.suppliers, D.locations, D.inventory, D.forecasts,
      D.categories, D.settings,
    ],
  },
  '/api/po-calculations/recommendations/': {
    reads: [
      D.recommendations, D.recommendationRun, D.suppliers, D.supplierLinks, D.items,
      D.locations, D.categories, D.connectors, D.settings,
    ],
    purgeOn: [D.recommendationRun],
  },
  '/api/customer-order-headers/': { reads: [D.items, D.locations, D.categories, D.settings] },
  '/api/forecasts/': { reads: [D.forecasts, D.items, D.locations, D.categories, D.settings] },
  '/api/reorder-levels/': { reads: [D.inventory, D.forecasts, D.items, D.locations, D.settings] },
  // The rows are the safety-stock run's results, which the run that writes
  // the placements' safety stock also writes.
  '/api/stock-calculations/results/': { reads: [D.inventory, D.locations] },
  // The rows are one forecast run's results beside each item-location's
  // names; only a run writes the results.
  '/api/forecast-generator/results/': { reads: [D.items, D.locations] },
  '/api/reports/classification/data/': {
    reads: [
      D.items, D.inventory, D.forecasts, D.locations, D.categories, D.supplierLinks,
      D.suppliers, D.settings,
    ],
  },
  '/api/reports/dead-stock/data/': {
    reads: [
      D.items, D.inventory, D.forecasts, D.locations, D.categories, D.suppliers,
      D.supplierLinks, D.dataHealth, D.settings,
    ],
  },
  '/api/reports/excess-items/data/': {
    reads: [
      D.inventory, D.items, D.forecasts, D.supplierLinks, D.suppliers, D.purchaseOrders,
      D.locations, D.categories, D.dataHealth, D.settings,
    ],
  },
  '/api/reports/gmroi/data/': {
    reads: [
      D.items, D.inventory, D.forecasts, D.purchaseOrders, D.locations, D.categories,
      D.dataHealth, D.settings,
    ],
  },
  '/api/reports/sales-forecast-accuracy/data/': {
    reads: [D.forecasts, D.items, D.inventory, D.locations, D.categories, D.settings],
  },
  '/api/reports/stockout-items/data/': {
    reads: [
      D.inventory, D.items, D.forecasts, D.supplierLinks, D.suppliers, D.locations,
      D.categories, D.purchaseOrders, D.recommendations, D.settings,
    ],
  },
};

/** What the grid behind `endpoint` reads: `{reads, purgeOn}`. */
export function gridReads(endpoint) {
  const entry = GRID_READS[endpoint];
  if (!entry) {
    throw new Error(`dataChanged: no GRID_READS entry for the grid endpoint ${endpoint}`);
  }
  return { reads: entry.reads, purgeOn: entry.purgeOn ?? [] };
}

/** Whether a change of `kinds` reaches a reader of `reads`. */
export function affects(kinds, reads) {
  if (kinds.includes(D.everything)) return true;
  const changed = kinds.includes(D.recommendationRun) ? [...kinds, D.recommendations] : kinds;
  return reads.some((kind) => changed.includes(kind));
}

/** Refuses anything but a list of kinds of DATA (an empty list is one). */
export function assertKinds(kinds) {
  if (!Array.isArray(kinds)) {
    throw new Error('dataChanged: the kinds of data changed are a list of DATA');
  }
  kinds.forEach((kind) => {
    if (!KINDS.has(kind)) throw new Error(`dataChanged: unknown kind of data ${String(kind)}`);
  });
}

function checkKinds(kinds) {
  assertKinds(kinds);
  if (kinds.length === 0) {
    throw new Error('dataChanged: a change names at least one kind of DATA');
  }
}

/** Refuses anything but a list of grid endpoints registered in GRID_READS. */
export function assertEdits(edits) {
  if (!Array.isArray(edits)) {
    throw new Error('dataChanged: the grids a write edited are a list of grid endpoints');
  }
  edits.forEach((endpoint) => gridReads(endpoint));
}

const listeners = new Set();

/**
 * A function that announces a change: `changed(kinds)` makes every reader of
 * those kinds read again and resolves once the queries on screen have.
 *
 * `edits` names the grid endpoints whose rows the write changed in place,
 * adding and removing none: those grids read their rows again by id and keep
 * them where they stand (`hooks/useSsrmGrid`), so a row whose sorted value
 * was just saved does not jump away from the merchant's cursor.
 */
export function useDataChanged() {
  const queryClient = useQueryClient();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  return useCallback((kinds, edits = []) => {
    checkKinds(kinds);
    assertEdits(edits);
    const reread = kinds.includes(D.everything)
      ? queryClient.invalidateQueries()
      : Promise.all(QUERY_READS
        .filter(([, reads]) => affects(kinds, reads))
        .map(([prefix]) => queryClient.invalidateQueries({ queryKey: prefix(storeId) })));
    listeners.forEach(({ reads, call }) => {
      if (affects(kinds, reads)) call(kinds, edits);
    });
    return reread.then(() => undefined);
  }, [queryClient, storeId]);
}

/**
 * Calls `listener(kinds, edits)` on each change that reaches a reader of
 * `reads`, while the calling component is mounted (`edits`: see
 * `useDataChanged`). The latest `listener` is called, so it may close over
 * render state.
 */
export function useOnDataChanged(reads, listener) {
  const listenerRef = useRef(listener);
  useEffect(() => {
    listenerRef.current = listener;
  });
  const readsKey = reads.join(' ');
  useEffect(() => {
    const entry = {
      reads: readsKey === '' ? [] : readsKey.split(' '),
      call: (kinds, edits) => listenerRef.current(kinds, edits),
    };
    listeners.add(entry);
    return () => listeners.delete(entry);
  }, [readsKey]);
}
